import { useEffect } from 'react';

import { Button } from 'fluxo-ui';
import { ArrowRightIcon, BarChartIcon, TrophyIcon, UserGroupIcon } from 'fluxo-ui/icons';

import { LiveQuizSessionSummary } from '../../../types/index.ts';
import useQuestionCountdown from '../../hooks/useQuestionCountdown';
import { useLiveQuizStore } from '../../store/liveQuizStore';

import AnswerDistribution from './AnswerDistribution';
import ConnectionBadge from './ConnectionBadge';
import HostQuestionPanel from './HostQuestionPanel';
import JoinCodePanel from './JoinCodePanel';
import Leaderboard from './Leaderboard';
import LiveQuizLobby from './LiveQuizLobby';

interface LiveQuizHostProps {
    session: LiveQuizSessionSummary;
}

const revealHoldMs = 6500;
const leaderboardHoldMs = 7500;

const LiveQuizHost: React.FC<LiveQuizHostProps> = ({ session }) => {
    const { participants, question, reveal, leaderboard, answerCount, connectionState, startQuiz, revealAnswer, showLeaderboard, nextQuestion, endSession } =
        useLiveQuizStore();

    const { secondsRemaining, progress } = useQuestionCountdown(question);

    const isLastQuestion = session.currentQuestionIndex + 1 >= session.totalQuestions;
    const responded = answerCount?.totalResponses ?? reveal?.stats.totalResponses ?? 0;

    const questionExpired = session.status === 'question' && !!question && secondsRemaining === 0;
    useEffect(() => {
        if (questionExpired) {
            revealAnswer();
        }
    }, [questionExpired, revealAnswer]);

    useEffect(() => {
        if (!session.settings.autoAdvance) {
            return;
        }

        if (session.status === 'reveal') {
            const timer = setTimeout(
                () => (session.settings.showLeaderboardAfterEachQuestion ? showLeaderboard() : nextQuestion()),
                revealHoldMs,
            );
            return () => clearTimeout(timer);
        }

        if (session.status === 'leaderboard') {
            const timer = setTimeout(() => (isLastQuestion ? endSession() : nextQuestion()), leaderboardHoldMs);
            return () => clearTimeout(timer);
        }
    }, [
        session.status,
        session.settings.autoAdvance,
        session.settings.showLeaderboardAfterEachQuestion,
        isLastQuestion,
        showLeaderboard,
        nextQuestion,
        endSession,
    ]);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
            <header className="sticky top-0 z-10 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <h1 className="truncate text-base font-semibold text-slate-900 dark:text-white">{session.examTitle}</h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            {session.status === 'waiting'
                                ? 'Waiting to start'
                                : `Question ${session.currentQuestionIndex + 1} of ${session.totalQuestions}`}
                        </p>
                    </div>

                    <div className="flex items-center gap-3 sm:gap-5">
                        <ConnectionBadge state={connectionState} />
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white">
                            <UserGroupIcon className="w-4 h-4 text-slate-400" />
                            <span className="tabular-nums">{participants.length}</span>
                        </span>
                        <Button label="End" variant="danger" size="sm" onClick={endSession} />
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
                {session.status === 'waiting' && (
                    <LiveQuizLobby
                        joinCode={session.joinCode}
                        participants={participants}
                        totalQuestions={session.totalQuestions}
                        onStart={startQuiz}
                    />
                )}

                {session.status !== 'waiting' && (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-6 items-start">
                        <div className="space-y-4">
                            {question && session.status === 'question' && (
                                <HostQuestionPanel
                                    question={question}
                                    secondsRemaining={secondsRemaining}
                                    progress={progress}
                                    responded={responded}
                                    totalParticipants={answerCount?.totalParticipants ?? participants.length}
                                    onReveal={revealAnswer}
                                />
                            )}

                            {reveal && (session.status === 'reveal' || session.status === 'leaderboard' || session.status === 'ended') && (
                                <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                                    <div className="flex items-baseline justify-between gap-3 mb-4">
                                        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">How everyone answered</h2>
                                        <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                                            {reveal.stats.correctResponses}/{reveal.stats.totalResponses} correct
                                        </span>
                                    </div>
                                    <AnswerDistribution stats={reveal.stats} />
                                </section>
                            )}

                            {session.status !== 'ended' && (
                                <div className="flex flex-wrap gap-2">
                                    {session.status === 'reveal' && (
                                        <Button
                                            label="Show leaderboard"
                                            leftIcon={BarChartIcon}
                                            variant="default"
                                            onClick={showLeaderboard}
                                        />
                                    )}
                                    {(session.status === 'reveal' || session.status === 'leaderboard') && (
                                        <Button
                                            label={isLastQuestion ? 'Finish quiz' : 'Next question'}
                                            rightIcon={isLastQuestion ? TrophyIcon : ArrowRightIcon}
                                            variant="primary"
                                            onClick={isLastQuestion ? endSession : nextQuestion}
                                        />
                                    )}
                                </div>
                            )}

                            {session.status === 'ended' && (
                                <section className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                                    <h2 className="mb-5 text-sm font-semibold text-slate-900 dark:text-white">Final results</h2>
                                    <Leaderboard entries={leaderboard} showPodium limit={20} />
                                </section>
                            )}
                        </div>

                        <aside className="lg:sticky lg:top-20 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Leaderboard
                            </h2>
                            <Leaderboard entries={leaderboard} />
                            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                                <JoinCodePanel joinCode={session.joinCode} compact />
                            </div>
                        </aside>
                    </div>
                )}
            </main>
        </div>
    );
};

export default LiveQuizHost;
